import { useTranslation } from "react-i18next"

import { CrewStatTile } from "@/components/crew/crew-stat-tile"
import { cn } from "@/lib/utils"

/**
 * OKS-25 — Crew quick-stats strip.
 *
 * Three shadcn-`Card` tiles rendered in a `grid-cols-1 sm:grid-cols-3`
 * layout per UX Spec §Layout. Tiles describe the **unfiltered** active
 * crew (PRD §Acceptance — filters narrow the roster but never the
 * stats).
 *
 * Wires (BE-locked prop names per CONTRACT FROZEN 2026-05-03):
 *   1. `Crew size`        → `crew-list-stat-size`       · empty: `0`
 *   2. `Avg engagement`   → `crew-list-stat-engagement` · empty: `—` + tooltip
 *   3. `Content produced` → `crew-list-stat-content`    · empty: `—` + tooltip
 *
 * `avg_engagement` is a percent with one decimal; `crew_size` is an
 * integer. `content_produced` is `null` in V1 — the tile ships with
 * the "Coming soon" tooltip; OKS-32 wires the value.
 *
 * Locked testid: `crew-list-stats` (strip wrapper, mirrors the
 * `candidate-list-*` muscle memory team-lead arbitrated to converge
 * UX Spec / FE TRD / QA TRD).
 */
interface CrewQuickStatsProps {
  stats: {
    crew_size: number
    avg_engagement: number | null
    content_produced: number | null
  }
  className?: string
}

export function CrewQuickStats({ stats, className }: CrewQuickStatsProps) {
  const { t } = useTranslation()

  return (
    <div
      data-testid="crew-list-stats"
      className={cn("grid grid-cols-1 gap-4 sm:grid-cols-3", className)}
    >
      <CrewStatTile
        testId="crew-list-stat-size"
        label={t("crew.stats.size.label")}
        value={String(stats.crew_size)}
        rawValue={stats.crew_size}
      />
      <CrewStatTile
        testId="crew-list-stat-engagement"
        tooltipTestId="crew-list-stat-engagement-tooltip"
        label={t("crew.stats.engagement.label")}
        value={
          stats.avg_engagement === null
            ? null
            : formatEngagementPercent(stats.avg_engagement)
        }
        rawValue={stats.avg_engagement}
        emptyTooltip={t("crew.stats.engagement.empty_tooltip")}
      />
      <CrewStatTile
        testId="crew-list-stat-content"
        tooltipTestId="crew-list-stat-content-tooltip"
        label={t("crew.stats.content.label")}
        value={
          stats.content_produced === null
            ? null
            : String(stats.content_produced)
        }
        rawValue={stats.content_produced}
        emptyTooltip={t("crew.stats.content.coming_soon_tooltip")}
      />
    </div>
  )
}

/**
 * Render `avg_engagement` as `5.2%` with one decimal. Backend emits a
 * `decimal(5,2)` so the wire value can carry two decimals; the tile
 * rounds to one for display tightness. Exported for direct unit
 * coverage.
 */
export function formatEngagementPercent(value: number): string {
  return `${value.toFixed(1)}%`
}
